// Locale -> string bundle loader. Bundles are imported statically so they
// land in the main JS chunk and are available without an extra fetch.
import fi from "./fi.json";
import enUs from "./en-us.json";
import type { Strings } from "./types.ts";

const bundles: Record<string, Strings> = {
  "fi": fi as unknown as Strings,
  "en-us": enUs as unknown as Strings,
  // Tenant-key alias: arcade_tenants stores "us" but the string bundle
  // ships as "en-us". Map "us" -> en-us so getStrings() resolves cleanly
  // without a fallback hop.
  "us": enUs as unknown as Strings,
};

export function getStrings(locale: string): Strings {
  return bundles[locale] ?? bundles["en-us"];
}

const LOCALE_RE = /^[a-z]{2}(-[a-z]{2})?$/i;

export function localeFromPath(): string {
  // /us/charge/ -> "us". /fi/charge/ -> "fi". /en-us/charge/ -> "en-us".
  // Anything that doesn't match the BCP-47 shape (lowercase) falls through
  // to the tenant locale, then to the default below. Default is "us" -- US
  // tenant is live in arcade_tenants and the en-us string bundle is the most
  // universal English copy, so visitors hitting bare /charge/ resolve to a
  // working manifest + readable strings.
  const seg = location.pathname.split("/").filter(Boolean)[0] ?? "";
  if (LOCALE_RE.test(seg)) return seg.toLowerCase();
  // Same-origin tenant mount: the browser URL is /pelit/charge, so the first
  // segment is the LOCALIZED GAMES SLUG, not a locale -- it can never match
  // above and every Finnish player would silently get the US puzzle in
  // English. The arcade server stamps the per-request tenant locale into
  // window.__TRIPPI_LOCALE__ (from Caddy's X-Trippi-Locale); honour it.
  // Lower priority than the path so an explicit /fi/charge deep link still
  // wins. Mirrors resolveLocale() in games/shared/i18n.ts.
  const tenant =
    (globalThis as { __TRIPPI_LOCALE__?: string }).__TRIPPI_LOCALE__;
  if (tenant && LOCALE_RE.test(tenant)) return tenant.toLowerCase();
  return "us";
}

export function t(
  locale: string,
  key: keyof Strings,
  vars: Record<string, string | number> = {},
): string {
  const raw = getStrings(locale)[key] ?? String(key);
  return raw.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ""));
}
